import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'

import {
  getCheapestPurchasableOption,
  getIsTourOfferPurchasable,
  getSortedItinerary,
} from 'selectors/TourV2/tourV2OfferPageSelectors'
import type { OfferPageState, TourV2MonthFilter } from 'contexts/OfferPage/offerPageStateReducer'
import {
  EXCLUDE_OFFER_PROMO_SPEND_EARN,
  TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS,
  TourV2ContentSections,
} from 'constants/tours'
import useTourV2ContentSectionLabels from 'hooks/TourV2/useTourV2ContentSectionLabels'
import delay from 'lib/utils/delay'
import determineTourV2OfferContentSections from 'lib/tours/determineTourV2OfferContentSections'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import BodyText from 'LuxKit/Typography/BodyText'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import useOnScreen from 'hooks/useOnScreen'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import TourV2Departures from './Departures/TourV2Departures'
import TourV2FinePrint from './FinePrint/TourV2FinePrint'
import TourV2HeadData from './TourV2HeadData'
import TourV2Inclusions from './Inclusions/TourV2Inclusions'
import TourV2ItineraryAndMap from './Itinerary/TourV2ItineraryAndMap'
import TourV2Variations from './TourVariations/TourV2Variations'
import TourV2Overview from './TourV2Overview'
import TourV2StickyHeader from './StickyElements/TourV2StickyHeader'
import TourV2FAQ from './FAQ/TourV2FAQ'
import TourV2BookWithConfidence from './BookWithConfidence/TourV2BookWithConfidence'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TourV2UnavailabilityCard from './TourV2UnavailabilityCard'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { getOfferPalette, isSalesforceTourV2 } from 'lib/offer/offerUtils'
import TourV2YouMayAlsoLikeCarrousel from './YouMayAlsoLike/TourV2YouMayAlsoLikeCarrousel'
import UserReviewList from '../NavigableOfferContent/UserReviewList'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { trackEvent } from 'analytics/analytics'
import useTourV2Details from 'hooks/TourV2/useTourV2Details'
import OfferPageGallery from '../OfferPageGallery'
import { offerPageHotLeadEvent } from 'analytics/snowplow/events'
import TourV2StickyFooter from './StickyElements/TourV2StickyFooter'
import usePrevious from 'hooks/usePrevious'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import Group from 'components/utils/Group'
import LuxPlusNotificationBanner from 'luxPlus/components/Banners/LuxPlusNotificationBanner'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import AdditionalWaysToExperienceSection from './AdditionalWaysToExperience/AdditionalWaysToExperienceSection'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useInView } from 'react-intersection-observer'
import { objectKeys } from 'lib/object/objectUtils'
import { isCruiseFlashOfferV2 } from 'lib/offer/offerTypes'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useMountedEffect from 'hooks/useMountedEffect'
import TourSpendEarnPageBanner from 'components/Tours/TourSpendEarnPageBanner'
import { isTourPromoSpendEarnEnabled } from 'selectors/TourV2/toursSelectors'
import SiteTakeoverOfferPageBanner from 'takeovers/utils/components/SiteTakeoverOfferPageBanner'
import { getIsTourGenericItineraryPDFEnabled } from 'checkout/selectors/tourV2Selectors'
import { downloadPdf, redirectToPdf } from 'lib/web/downloadPdf'
import { createGenericTourItineraryPDFUrl, downloadGenericTourItineraryPDFUrl } from 'api/tour/tourV2'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import LineFileCheckIcon from 'LuxKit/Icons/line/LineFileCheckIcon'
import LineFileDownloadIcon from 'LuxKit/Icons/line/LineFileDownloadIcon'
import TourV2SectionTitle from './TourV2SectionTitle'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import { shouldShowAdditionalTourWays } from 'lib/tours/tourUtils'
import ContactUSP from '../ContactUSP'
import useShareModalConfig from 'hooks/useShareModalConfig'
import { hasCustomisedDownloadHandler, isAndroidAppUserAgent } from 'lib/web/deviceUtils'
import TourV2SimilarTourCarousel from './TourV2SimilarTourCarousel'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import TourCrossPromoBanner from 'components/Tours/TourCrossPromoBanner'
import TourV2VariationPackages from './TourVariations/TourV2VariationPackages'
import { getTourV2SelectedPackageType } from 'selectors/offerSelectors'
import { setTourV2SelectedPackageType } from 'actions/tour/TourV2Actions'

const UnavailabilitySectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};
`

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};

  > .section-title + .section-subtitle {
    margin-top: ${rem(8)}
  }

  > .section-title + .section-content,
  > .section-subtitle + .section-content {
    margin-top: ${rem(32)};
  }
`

const OverviewSectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(24)};
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(64)};
  }
`

// We're using a 8px high, full width 'divider'. There is no lux kit
// equiv at the moment, so fake it with a small pane
const SectionDivider = styled(Pane)`
  height: ${rem(8)};
`

const MainContent = styled(Group)`
  flex-grow: 1;
`

interface MappedProps {
  cheapestPurchasableOption?: App.Tours.TourV2OfferPurchasableOption
  isOfferPurchasable: boolean
  itinerary: Array<App.Tours.TourV2OfferItineraryItem>
  offer: App.Tours.TourV2Offer
  rooms?: OfferPageState['rooms']
  departureMonth?: TourV2MonthFilter
  variation: App.Tours.TourV2OfferVariation
  isTourGenericItineraryPDFAvailable: boolean
}

interface Props {
}

interface SectionAnchor {
  end: HTMLDivElement | null
  start: HTMLDivElement | null
}

function TourV2Content(props: Props & MappedProps) {
  const {
    cheapestPurchasableOption,
    isOfferPurchasable,
    itinerary,
    offer,
    rooms,
    variation,
    departureMonth,
    isTourGenericItineraryPDFAvailable,
  } = props

  const { finePrint, faq } = offer

  const [activeSection, setActiveSection] = useState<TourV2ContentSections | undefined>()
  const [scrollTarget, setScrollTarget] = useState<TourV2ContentSections | null>(null)
  const dispatch = useAppDispatch()
  const selectedTourPackageType = useAppSelector((state) => getTourV2SelectedPackageType(state, offer.id))
  const setSelectedTourPackageType = useCallback((packageType: string | undefined) => {
    dispatch(setTourV2SelectedPackageType(offer.id, packageType))
  }, [dispatch, offer.id])

  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const promoSpendEarnEnabled = useAppSelector(state => isTourPromoSpendEarnEnabled(state))
  const region = useAppSelector((state) => state.geo.currentRegionCode)
  const isCruiseFlashOffer = isCruiseFlashOfferV2(offer)

  const galleryAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const overviewAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const variationsAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const preferredPackagesAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const itineraryAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const inclusionsAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const departuresDatesAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const additionalWaysAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const similarTourAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const finePrintAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const faqAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const reviewsAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const footerAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const monthCarouselAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const [monthCarouselRef, monthCarouselInView] = useInView({ rootMargin: '-120px 0px 0px 0px' })

  const sectionsVisible: Record<TourV2ContentSections, boolean> = {
    [TourV2ContentSections.GALLERY]: useOnScreen(galleryAnchor.current?.start, galleryAnchor.current?.end, -120, 120),
    [TourV2ContentSections.OVERVIEW]: useOnScreen(overviewAnchor.current?.start, overviewAnchor.current?.end, -120, 120),
    [TourV2ContentSections.VARIATIONS]: useOnScreen(variationsAnchor.current?.start, variationsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.PREFERRED_PACKAGES]: useOnScreen(preferredPackagesAnchor.current?.start, preferredPackagesAnchor.current?.end, -120, 120),
    [TourV2ContentSections.ITINERARY]: useOnScreen(itineraryAnchor.current?.start, itineraryAnchor.current?.end, -120, 120),
    [TourV2ContentSections.DEPARTURES]: useOnScreen(departuresDatesAnchor.current?.start, departuresDatesAnchor.current?.end, -120, 120),
    [TourV2ContentSections.ADDITIONAL_WAYS]: useOnScreen(additionalWaysAnchor.current?.start, additionalWaysAnchor.current?.end, -120, 120),
    [TourV2ContentSections.INCLUSIONS]: useOnScreen(inclusionsAnchor.current?.start, inclusionsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.FINE_PRINT]: useOnScreen(finePrintAnchor.current?.start, finePrintAnchor.current?.end, -120, 120),
    [TourV2ContentSections.FAQ]: useOnScreen(faqAnchor.current?.start, faqAnchor.current?.end),
    [TourV2ContentSections.REVIEWS]: useOnScreen(reviewsAnchor.current?.start, reviewsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.YOU_MAY_ALSO_LIKE]: false,
    [TourV2ContentSections.DEPARTURE_DATES]: false,
    [TourV2ContentSections.SIMILAR_TOURS]: false,
  }

  const hasGotToOverview = useHasGotToAnchor(overviewAnchor.current?.start, -110)
  const hasGotToDepartures = useHasGotToAnchor(departuresDatesAnchor.current?.start, -136)
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0)
  const finePrintSectionRef = useRef<HTMLDivElement | null>(null)
  const hasGotToFinePrint = useHasGotToAnchor(finePrintSectionRef.current, -stickyHeaderHeight)

  const scrollToAnchor = useCallback(
    (section: TourV2ContentSections) => {
      const sectionAnchorsRefs: Record<TourV2ContentSections, React.MutableRefObject<SectionAnchor> | undefined> = {
        [TourV2ContentSections.GALLERY]: galleryAnchor,
        [TourV2ContentSections.OVERVIEW]: overviewAnchor,
        [TourV2ContentSections.VARIATIONS]: variationsAnchor,
        [TourV2ContentSections.PREFERRED_PACKAGES]: preferredPackagesAnchor,
        [TourV2ContentSections.ITINERARY]: itineraryAnchor,
        [TourV2ContentSections.DEPARTURES]: departuresDatesAnchor,
        [TourV2ContentSections.ADDITIONAL_WAYS]: additionalWaysAnchor,
        [TourV2ContentSections.INCLUSIONS]: inclusionsAnchor,
        [TourV2ContentSections.FINE_PRINT]: finePrintAnchor,
        [TourV2ContentSections.FAQ]: faqAnchor,
        [TourV2ContentSections.REVIEWS]: reviewsAnchor,
        [TourV2ContentSections.YOU_MAY_ALSO_LIKE]: undefined,
        [TourV2ContentSections.DEPARTURE_DATES]: monthCarouselAnchor,
        [TourV2ContentSections.SIMILAR_TOURS]: similarTourAnchor,
      }
      if (section !== TourV2ContentSections.DEPARTURE_DATES) {
        // departure dates isn't a real section, we don't scroll target it
        setScrollTarget(section)
      }
      sectionAnchorsRefs[section]!.current?.start?.scrollIntoView()
    },
    [setScrollTarget],
  )

  const contentSections = useMemo(() =>
    determineTourV2OfferContentSections(offer, region, variation),
  [offer, variation, region])

  const headerContentSections = useMemo(() => [...contentSections].filter(section => TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS.has(section)),
    [contentSections])

  const currentOnScreenSection = objectKeys(sectionsVisible).find((section) => sectionsVisible[section] && (!scrollTarget || section === scrollTarget)) as TourV2ContentSections

  const intl = useIntl()
  const TOUR_V2_CONTENT_SECTION_LABELS = useTourV2ContentSectionLabels()
  const acknowledgeVariarionChange = useCallback((variationId: string) => {
    const viewingVariation = offer.variations[variationId]
    delay(500).then(() => showSnackbar(
      intl.formatMessage(
        {
          id: 'kySASxcKIYN53GxunMx6',
          defaultMessage: 'You’re now viewing {variationName}',
          description: 'Snackbar message when user switches to a different tour variation',
        },
        { variationName: viewingVariation.name },
      ),
      'success',
    ))
  }, [offer, intl])

  useEffect(
    () => {
      const hasScrolledToTargetSection = !scrollTarget || currentOnScreenSection === scrollTarget
      if (hasScrolledToTargetSection) {
        setActiveSection(currentOnScreenSection)
        setScrollTarget(null)
      }
    },
    [scrollTarget, currentOnScreenSection],
  )

  const paletteValue = getOfferPalette(offer, luxPlusEnabled)
  const { hasAlternativeRoute } = useTourV2Details(offer)

  const previousSelectedDepartureMonth = usePrevious(departureMonth)
  const expressedDateInterest = (departureMonth != previousSelectedDepartureMonth) && (previousSelectedDepartureMonth != undefined)

  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)
  const isEurope = useEuropeOfferPageWalledGarden()

  useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    occupancy: rooms,
    variant: Object.keys(offer.variations).length > 1 ? variation?.fkTourOptionId : undefined,
    year: expressedDateInterest && departureMonth ? departureMonth.year : undefined,
    month: expressedDateInterest && departureMonth ? departureMonth.month + 1 : undefined,
  })
  const offerPageState = useContext(OfferPageStateContext)
  useMountedEffect(() => {
    if (offerPageState.rooms && offerPageState.departureMonth) {
      trackEvent(offerPageHotLeadEvent(offer, offerPageState))
    }
  }, [offerPageState.rooms, offerPageState.departureMonth])

  const alternativeVariation = useMemo(() => {
    if (hasAlternativeRoute) {
      const alternativeVariationId = Object.keys(offer.variations).filter((variationId) => variationId !== variation?.id)[0]
      return offer.variations[alternativeVariationId]
    }
  }, [variation, hasAlternativeRoute, offer.variations])

  // we only recommend tours with time filter when user specifically select a departure time rather than using the default departure time
  const defaultDeparture = useRef('')
  const [recommendDepartureYear, recommendDepartureMonth] = useMemo(() => {
    if (departureMonth) {
      const selectedDeparture = `${departureMonth.year}-${departureMonth.month}`
      if (!defaultDeparture.current) {
        defaultDeparture.current = selectedDeparture
      }
      if (selectedDeparture !== defaultDeparture.current) {
        return [departureMonth.year, departureMonth.month]
      }
    }
    return [undefined, undefined]
  }, [departureMonth])

  const shouldShowBanner = useShouldShowBanner({ location: 'offer', vertical: 'tours', offer })

  const shareConfig = useShareModalConfig({
    offerId: offer.id,
    location: offer.name,
    type: 'offer',
    utmCampaign: 'webshare',
    offerType: 'tour_v2',
  })

  const [isPdfDownloading, setIsPdfDownloading] = useState(false)
  const [isPdfDownloadSuccess, setPdfDownloadSuccess] = useState(false)

  const downloadPdfItineraryLink = useCallback(async(ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    setIsPdfDownloading(true)
    try {
      const tourId = offer.id
      const tourOptionId = variation.fkTourOptionId

      const userAgent = navigator.userAgent || ''
      const isAndroidApp = isAndroidAppUserAgent(userAgent)
      const shouldUseNativeDownloadHandler = hasCustomisedDownloadHandler()

      if (isAndroidApp && !shouldUseNativeDownloadHandler) {
        // HACK: pass API PDF link directly for Android app as fallback
        // can only use Google Docs for rendering PDF under this case, which may not work for all customers
        redirectToPdf(createGenericTourItineraryPDFUrl(tourId, tourOptionId, region))
        setPdfDownloadSuccess(true)
        showSnackbar(intl.formatMessage({
          id: '0GzW4-g-fEu-sPpRwkMn',
          defaultMessage: 'Preparing itinerary PDF...',
          description: 'Snackbar message when preparing PDF download in the itinerary',
        }), 'success')
      } else {
        const blob = await downloadGenericTourItineraryPDFUrl(tourId, tourOptionId, region)
        const fileName = `Tour-${tourId}-${tourOptionId}-${region}-itinerary.pdf`
        downloadPdf(blob, fileName)
        setPdfDownloadSuccess(true)
        showSnackbar(intl.formatMessage({
          id: 'L7Kx8IsCmdLyd_NvkD9I',
          defaultMessage: 'Itinerary PDF downloaded.',
          description: 'Snackbar message when PDF download succeeds in the itinerary',
        }), 'success')
      }
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'KjarQlcl6XKksOAwYmFX',
        defaultMessage: 'Failed to download itinerary PDF.',
        description: 'Snackbar message when PDF download fails in the itinerary',
      }), 'critical')
      setPdfDownloadSuccess(false)
    } finally {
      setIsPdfDownloading(false)
    }
  }, [offer.id, region, variation.fkTourOptionId, intl])

  const isDownloadPdfLinkAvailable = useMemo<boolean>(() => {
    return isTourGenericItineraryPDFAvailable && !!(offer.source === 'salesforce') && !isCruiseFlashOffer
  }, [isTourGenericItineraryPDFAvailable, offer.source, isCruiseFlashOffer])

  const itinerarySectionTitle = variation?.routeMapImage
    ? intl.formatMessage({
        id: 'P1Bhl9YqU-mb05JMXbm2',
        defaultMessage: 'Itinerary & map',
        description: 'Section title for itinerary with route map available',
      })
    : TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.ITINERARY]

  return (<MainContent direction="vertical" verticalAlign="space-between">
    <div>
      <ProductPaletteProvider product={offer}>
        <TourV2HeadData offer={offer} variation={variation} />
        <TourV2StickyHeader
          contentSections={headerContentSections}
          onScrollTo={scrollToAnchor}
          purchasableOption={cheapestPurchasableOption}
          visible={hasGotToOverview && !hasGotToFinePrint}
          title={variation?.name}
          activeSection={activeSection}
          buttonPaletteValue={paletteValue}
          monthCarouselInView={monthCarouselInView}
          variation={variation}
          onHeightChange={setStickyHeaderHeight}
        />

        {luxPlusEnabled && !!offer.luxPlus?.access &&
          <LuxPlusNotificationBanner>
            {offer.luxPlus.access === 'earlyAccess' && intl.formatMessage({
              id: '_KijQhnUXuJT-wcLSjM7',
              defaultMessage: 'Members get early access to this offer before the general public and more',
              description: 'LuxPlus early access banner message on the offer listing or detail page',
            })}
            {offer.luxPlus.access === 'memberOnly' && intl.formatMessage({
              id: 'foBJkQq3A6lReMi_ze95',
              defaultMessage: 'You get exclusive access to this member-only offer',
              description: 'LuxPlus member-only access banner message on the offer page',
            })}
          </LuxPlusNotificationBanner>}
        {promoSpendEarnEnabled && isSalesforceTourV2(offer) && !EXCLUDE_OFFER_PROMO_SPEND_EARN.includes(offer.id) && <TourSpendEarnPageBanner />}

        <div id="TOUR_V2_CONTENT_TOP_ANCHOR" aria-hidden="true" />

        {shouldShowBanner && <SiteTakeoverOfferPageBanner vertical="tours" />}
        {!isOfferPurchasable && !!variation && <Pane>
          <UnavailabilitySectionContainer>
            <Group direction="vertical" gap={40}>
              <TourV2UnavailabilityCard offer={offer} offerVariation={variation} />
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <Heading variant="heading3" colour="normal-contrast">
                  {intl.formatMessage({
                    id: 'kMtjcIOL1IXEUWOa6nLo',
                    defaultMessage: 'More offers available',
                    description: 'Heading when tour offer is unavailable on the tour offer detail page',
                  })}
                </Heading>
                <TextButton kind="secondary" to="/tours">
                  {intl.formatMessage({
                    id: 'K8g_FjvoRmrRbbpozk2N',
                    defaultMessage: 'Browse all tour offers',
                    description: 'Button text to browse all tour offers on the tour offer detail page',
                  })}
                </TextButton>
              </Group>
            </Group>
          </UnavailabilitySectionContainer>
        </Pane>}
        {contentSections.has(TourV2ContentSections.GALLERY) && <>
          <ScrollAnchor ref={node => { galleryAnchor.current.start = node }} />
          <OfferPageGallery
            offer={offer}
            tourVariation={variation}
            shareModalConfig={shareConfig}
            autoPlayVideo
          />
          <ScrollAnchor ref={node => { galleryAnchor.current.end = node }} />
        </>}

        <ProductPaletteProvider palette={paletteValue}>
          <LuxPlusLabelProvider page="offer-page">
            <Pane>

              <OverviewSectionContainer>
                <ScrollAnchor ref={node => { overviewAnchor.current.start = node }} />
                <TourV2Overview
                  cheapestPurchasableOption={cheapestPurchasableOption}
                  onView={scrollToAnchor}
                  buttonPaletteValue={paletteValue}
                  shareModalConfig={shareConfig}
                />
                <ScrollAnchor ref={node => { overviewAnchor.current.end = node }} />
              </OverviewSectionContainer>
              <SoftOfferPageWalledGarden>
                {contentSections.has(TourV2ContentSections.VARIATIONS) && <SectionContainer size="lg">
                  <ScrollAnchor ref={node => { variationsAnchor.current.start = node }} />
                  <Heading variant="heading3" align="start" className="section-title">
                    {intl.formatMessage({
                      id: 'D11AAbmc_8Q7KeDT3Re_',
                      defaultMessage: 'Choose a tour option',
                      description: 'Section heading for tour variations on the tour offer detail page',
                    })}
                  </Heading>
                  <div className="section-content">
                    <TourV2Variations onVariationChanged={acknowledgeVariarionChange} />
                  </div>
                  <ScrollAnchor ref={node => { variationsAnchor.current.end = node }} id="TOUR_V2_ITINERARY_TOP_ANCHOR" />
                  {
                    offer.productType === 'custom_tour' &&
                    <Pane padding="20 0">
                      <ContactUSP
                        title={intl.formatMessage({
                          id: 'MlUAfXKmR72besOUX8TE',
                          defaultMessage: 'Want to know more? Our dedicated support team is on hand 24/7',
                          description: 'Contact USP title for custom tours on the tour offer detail page',
                        })}
                        description={intl.formatMessage({
                          id: '7zuSsbi17387Tp33uez1',
                          defaultMessage: 'Give us a call to answer questions and help you plan, obligation free',
                          description: 'Contact USP description for custom tours on the tour page',
                        })}
                      />
                    </Pane>
                  }
                </SectionContainer>}
                {contentSections.has(TourV2ContentSections.PREFERRED_PACKAGES) && <SectionContainer size="lg">
                  <ScrollAnchor ref={node => { preferredPackagesAnchor.current.start = node }} />
                  <Heading variant="heading3" align="start" className="section-title">
                    {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.PREFERRED_PACKAGES]}
                  </Heading>
                  <div className="section-content">
                    <TourV2VariationPackages
                      offer={offer}
                      variation={variation}
                      selectedPackageType={selectedTourPackageType}
                      onSelectedPackageTypeChange={setSelectedTourPackageType}
                    />
                  </div>
                  <ScrollAnchor ref={node => { preferredPackagesAnchor.current.end = node }} />
                </SectionContainer>}
                {contentSections.has(TourV2ContentSections.ITINERARY) && <SectionContainer
                  size={!variation?.routeMapImage ? 'md' : undefined}
                >
                  <ScrollAnchor ref={node => { itineraryAnchor.current.start = node }} />
                  <TourV2SectionTitle
                    className="section-title"
                    end={
                      <div>
                        {
                          isDownloadPdfLinkAvailable &&
                          <TextButton kind="secondary" startIcon={isPdfDownloading ? <LoadingSpinner /> : (isPdfDownloadSuccess && <LineFileCheckIcon />) || <LineFileDownloadIcon />} onClick={downloadPdfItineraryLink} disabled={isPdfDownloading}>
                            {intl.formatMessage({
                              id: 'aoCKjafRlM7hpX9GofXn',
                              defaultMessage: 'Download Itinerary',
                              description: 'Button text to download itinerary PDF on the itinerary or schedule view',
                            })}
                          </TextButton>
                        }
                      </div>
                    }
                  >
                    {itinerarySectionTitle}
                  </TourV2SectionTitle>
                  <div className="section-content">
                    <TourV2ItineraryAndMap
                      itinerary={itinerary}
                      variation={variation}
                      offerType={offer.type}
                      alternativeVariation={alternativeVariation}
                      onAlternativeRouteChange={acknowledgeVariarionChange}
                    />
                  </div>
                  <ScrollAnchor ref={node => { itineraryAnchor.current.end = node }} />
                </SectionContainer>}
              </SoftOfferPageWalledGarden>

              <SoftOfferPageWalledGarden>
                {contentSections.has(TourV2ContentSections.INCLUSIONS) && <SectionContainer size="md">
                  <ScrollAnchor ref={node => { inclusionsAnchor.current.start = node }} />
                  <Heading variant="heading2" align="center" className="section-title">
                    {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.INCLUSIONS]}
                  </Heading>

                  {!isCruiseFlashOffer && <BodyText variant="large" align="center" className="section-subtitle">
                    {intl.formatMessage({
                      id: '1rgz7R00MuCLnZCX6HW8',
                      defaultMessage: 'The following are included in your tour package:',
                      description: 'Subtitle for tour inclusions section on the tour offer detail page',
                    })}
                  </BodyText>}

                  {isCruiseFlashOffer && <BodyText variant="large" align="center" className="section-subtitle">
                    {intl.formatMessage({
                      id: '8wmbAkdKshmZlZO7dIzo',
                      defaultMessage: 'The following are included in your cruise package:',
                      description: 'Subtitle for cruise inclusions section in the cruise offer or booking section',
                    })}
                  </BodyText>}

                  <div className="section-content">
                    <TourV2Inclusions
                      diningInclusions={variation?.diningInclusions}
                      travelInclusions={variation?.travelInclusions}
                    />
                  </div>
                  <ScrollAnchor ref={node => { inclusionsAnchor.current.end = node }} />
                </SectionContainer>}
              </SoftOfferPageWalledGarden>
            </Pane>
            <SoftOfferPageWalledGarden>
              <SectionDivider background="light-background" />
              <Pane>
                <ScrollAnchor ref={node => { departuresDatesAnchor.current.start = node }} id="TOUR_DEPARTURE_DATES" />
                <VerticalSpacer gap={32}>
                  <SectionContainer size="lg">
                    <Heading variant="heading2" align="center" className="section-title">
                      {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.DEPARTURES]}
                    </Heading>
                  </SectionContainer>
                  <TourV2Departures
                    monthCarouselRef={node => {
                      monthCarouselAnchor.current.start = node
                      monthCarouselRef(node)
                    }}
                    variation={variation}
                    packageType={selectedTourPackageType}
                  />
                  <ScrollAnchor ref={node => { departuresDatesAnchor.current.end = node }} />
                </VerticalSpacer>
              </Pane>
              {
                offer.productType === 'custom_tour' &&
                <Pane padding="25 0">
                  <LayoutContainer>
                    <ContactUSP
                      title={intl.formatMessage({
                        id: 'MlUAfXKmR72besOUX8TE',
                        defaultMessage: 'Want to know more? Our dedicated support team is on hand 24/7',
                        description: 'Contact USP title for custom tours on the tour offer detail page',
                      })}
                      description={intl.formatMessage({
                        id: '7zuSsbi17387Tp33uez1',
                        defaultMessage: 'Give us a call to answer questions and help you plan, obligation free',
                        description: 'Contact USP description for custom tours on the tour page',
                      })}
                    />
                  </LayoutContainer>
                </Pane>
              }
            </SoftOfferPageWalledGarden>
            {offer.similarTours && offer.similarTours.length > 0 && (
              <div>
                <SoftOfferPageWalledGarden>
                  <SectionDivider background="light-background" />
                </SoftOfferPageWalledGarden>
                <SoftOfferPageWalledGarden>
                  <Pane padding="40 0">
                    <VerticalSpacer gap={24}>
                      <LayoutContainer>
                        <Heading variant="heading3" align="start" className="section-title">
                          {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.SIMILAR_TOURS]}
                        </Heading>
                      </LayoutContainer>
                      <TourV2SimilarTourCarousel similarTourIds={offer.similarTours} />
                    </VerticalSpacer>
                  </Pane>
                </SoftOfferPageWalledGarden>
              </div>
            )}
            <SoftOfferPageWalledGarden>
              <SectionDivider background="light-background" />
            </SoftOfferPageWalledGarden>
          </LuxPlusLabelProvider>

          <SoftOfferPageWalledGarden>
            <Pane>
              {!isCruiseFlashOffer && contentSections.has(TourV2ContentSections.ADDITIONAL_WAYS) && shouldShowAdditionalTourWays(offer.productType) && <SectionContainer size="md">
                <ScrollAnchor ref={node => { additionalWaysAnchor.current.start = node }} />
                <Heading variant="heading3" align="start" className="section-title">
                  {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.ADDITIONAL_WAYS]}
                </Heading>
                <div className="section-content">
                  <AdditionalWaysToExperienceSection />
                </div>
                <ScrollAnchor ref={node => { additionalWaysAnchor.current.end = node }} />
              </SectionContainer>}

              <SectionContainer ref={finePrintSectionRef} size="md">
                <ScrollAnchor ref={node => { finePrintAnchor.current.start = node }} />
                <Heading variant="heading2" align="center" className="section-title">
                  {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.FINE_PRINT]}
                </Heading>
                <div className="section-content">
                  <TourV2FinePrint finePrint={finePrint} />
                </div>
                <ScrollAnchor ref={node => { finePrintAnchor.current.end = node }} />
              </SectionContainer>

              {contentSections.has(TourV2ContentSections.REVIEWS) && (
                <SectionContainer size="md">
                  <ScrollAnchor ref={node => { reviewsAnchor.current.start = node }} />
                  <div className="section-content">
                    <UserReviewList tour={offer} />
                  </div>
                  <ScrollAnchor ref={node => { reviewsAnchor.current.end = node }} />
                </SectionContainer>
              )}

              {contentSections.has(TourV2ContentSections.FAQ) && <SectionContainer size="md">
                <ScrollAnchor ref={node => { faqAnchor.current.start = node }} />
                <Heading variant="heading2" align="center" className="section-title">
                  {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.FAQ]}
                </Heading>
                <div className="section-content">
                  <TourV2FAQ faq={faq} />
                </div>
                <ScrollAnchor ref={node => { faqAnchor.current.end = node }} />
              </SectionContainer>}

              <TourCrossPromoBanner tourId={offer.id} />

              {contentSections.has(TourV2ContentSections.YOU_MAY_ALSO_LIKE) && <SectionContainer as="div">
                <div className="section-content">
                  <TourV2YouMayAlsoLikeCarrousel
                    offerId={offer.id}
                    trackingDescription="Tours"
                    departureYear={recommendDepartureYear}
                    departureMonth={recommendDepartureMonth}
                  />
                </div>
              </SectionContainer>}

            </Pane>
            <ScrollAnchor ref={node => { footerAnchor.current.start = node }} />
            {isOfferPurchasable && <TourV2StickyFooter
              visible={!hasGotToDepartures}
              purchasableOption={cheapestPurchasableOption}
              onScrollTo={scrollToAnchor}
              variation={variation}
            />}

            {contentSections.has(TourV2ContentSections.FAQ) && <SectionContainer size="md">
              <div className="section-content">
                <TourV2BookWithConfidence />
              </div>
            </SectionContainer>}
          </SoftOfferPageWalledGarden>
        </ProductPaletteProvider>
      </ProductPaletteProvider>
    </div>
    <SoftOfferPageWalledGarden bypassWall={isEurope} wall={<OfferPageSignUpWall offerType="tour" saveUpToPercent={purchasableOptionDetails?.valuedAt ? purchasableOptionDetails.valuedAt?.savingPercentage : undefined} />} />
  </MainContent>)
}

function mapOfferPageStateToProps(offerPageState: OfferPageState, appState: App.State): MappedProps {
  return {
    cheapestPurchasableOption: getCheapestPurchasableOption(offerPageState, appState),
    itinerary: getSortedItinerary(offerPageState),
    offer: offerPageState.tourV2Offer!,
    rooms: offerPageState.rooms,
    departureMonth: offerPageState.departureMonth,
    variation: offerPageState.tourV2Variation!,
    isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
    isTourGenericItineraryPDFAvailable: getIsTourGenericItineraryPDFEnabled(appState),
  }
}

export default withOfferPageAndReduxState(mapOfferPageStateToProps, TourV2Content)
